import React, { useState, useContext, useLayoutEffect, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Table as MuiTable,
  Paper,
  TablePagination,
  CircularProgress
} from "@material-ui/core";
import { distinctUntilChanged } from "rxjs/operators";
import { Subject } from "rxjs";

import { ITable, ITableState, TableActionTypes } from "./interfaces";
import { TableContext, TABLE_INIT_STATE } from "./TableContext";
import { Column } from "./Column";
import { parseCell, sortFactory } from "./utils";
import { Icon } from "../icons/Icon";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    overflowX: "auto",
    padding: "2px",
    marginTop: "0.8em"
  },
  tableWrapper: {
    overflowX: "auto",
    position: "relative",
    minHeight: 140
  },
  table: {
    minWidth: 650
  },
  row: {
    cursor: "pointer"
  },
  cell: {
    padding: "10px 0px 10px 16px"
  },
  cellInner: {
    display: "flex"
  },
  emptyData: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    padding: "2em",
    color: "#ababab"
  },
  emptyIcon: {
    fontSize: "2em"
  },
  loading: {
    position: "absolute",
    width: "100%",
    height: "100%",
    top: 0,
    paddingTop: 50,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#ffffff52",
    color: "#ababab",
    "&>div": {
      marginBottom: 10
    }
  }
}));

export function Table<Row>(props: ITable<Row>) {
  const classes = useStyles();
  const [tableState, setTableState] = useState(TABLE_INIT_STATE);
  const [visibleData, setVisibleData] = useState<any[]>([]);
  const { dispatch, subscribe } = useContext(TableContext);

  useLayoutEffect(() => {
    const tableStateObserver$ = new Subject<ITableState>();
    const tableSubscription = tableStateObserver$
      .pipe(
        distinctUntilChanged(
          (next, prev) =>
            next.columns === prev.columns &&
            next.filterData === prev.filterData &&
            next.rowsPerPage === prev.rowsPerPage &&
            next.page === prev.page &&
            next.sorting === prev.sorting
        )
      )
      .subscribe(state => {
        setTableState(state);
        const { sorting, filterData } = state;

        let sortedVisibleData = [...filterData];
        if (sorting && sorting.sortDirection) {
          const column = state.columns.find(c => c.id === sorting.id);
          if (!column) throw new Error(`Invalid column ${sorting.id}`);
          const { parse, type } = column;
          const sortFn = sortFactory(sorting.sortDirection);

          if (type === "date" || type === "number" || !parse) {
            sortedVisibleData.sort((a, b) => {
              return sortFn(a[sorting.id], b[sorting.id]);
            });
          } else {
            sortedVisibleData.sort((a, b) => {
              return sortFn(parse(a[sorting.id]), parse(b[sorting.id]));
            });
          }
        }

        const nextVisibleData = sortedVisibleData.slice(
          state.page * state.rowsPerPage,
          (state.page + 1) * state.rowsPerPage
        );
        setVisibleData(nextVisibleData);
      });

    const subscription = subscribe(tableStateObserver$);

    return () => {
      tableStateObserver$.complete();
      subscription.unsubscribe();
      tableSubscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch({
      type: TableActionTypes.init,
      payload: {
        rowsPerPage: props.rowsPerPage,
        columns: props.columns,
        data: props.data,
        sorting: props.sorting,
        loading: props.loading,
        id: props.id
      }
    });
  }, [props.loading, props.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classes.root}>
      <Paper className={classes.tableWrapper}>
        <MuiTable>
          <TableHead>
            <TableRow>
              {tableState.columns
                .filter(c => !c.hide)
                .map(column => (
                  <Column key={column.id} {...column} />
                ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleData.map(row => (
              <TableRow
                key={props.getRowId(row)}
                className={classes.row}
                onClick={event => {
                  dispatch({
                    type: TableActionTypes.onRowClick,
                    payload: {
                      position: { left: event.clientX, top: event.clientY },
                      row
                    }
                  });
                }}
                {...(props.tableRowProps ? props.tableRowProps(row) : {})}
              >
                {tableState.columns
                  .filter(c => !c.hide)
                  .map(column => (
                    <TableCell
                      key={column.id}
                      align="left"
                      classes={{ root: classes.cell }}
                    >
                      <div className={classes.cellInner}>
                        {column.renderCell
                          ? column.renderCell(row[column.id], row)
                          : parseCell(row[column.id], column)}
                      </div>
                    </TableCell>
                  ))}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>

        {tableState.loading ? (
          <div className={classes.loading}>
            <CircularProgress size={18} />
            Loading ...
          </div>
        ) : (
          visibleData.length === 0 && (
            <div className={classes.emptyData}>
              <Icon name="drafts" className={classes.emptyIcon} /> No Data
            </div>
          )
        )}
      </Paper>
      <TablePagination
        component="div"
        count={tableState.filterData.length}
        rowsPerPage={tableState.rowsPerPage}
        page={tableState.page}
        backIconButtonProps={{
          "aria-label": "Previous Page"
        }}
        nextIconButtonProps={{
          "aria-label": "Next Page"
        }}
        onChangePage={(event: any, page: number) => {
          dispatch({
            type: TableActionTypes.onChangePage,
            payload: page
          });
        }}
        onChangeRowsPerPage={(event: any) => {
          dispatch({
            type: TableActionTypes.onChangeRowsPerPage,
            payload: event.target.value
          });
        }}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
      />
    </div>
  );
}
